import React from 'react';
import { ChevronDown, ChevronUp, Speaker, Check } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors" data-testid="btn-toggle-sonorizacion">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">{badge}</span>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 bg-[#080c2d]/50">{children}</div>}
    </div>
  );
}

const PACKAGES = [
  {
    id: null,
    name: 'Sin Sonorización',
    price: 0,
    capacity: '',
    image: null,
    features: [],
  },
  {
    id: 'sono_essential',
    name: 'Essential',
    price: 3500,
    capacity: 'Hasta 250 personas',
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTpOgiKJmaznNTZfvtW9tjbUcz1rBtUdE6-ig&s',
    features: [
      '1 Micrófono inalámbrico de mano Shure PG',
      'Bocinas 350W Yamaha StagePas 350W',
      'Setup y operación incluidos',
    ],
  },
  {
    id: 'sono_plus',
    name: 'Plus',
    price: 7500,
    capacity: 'Hasta 500 personas',
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ7l1YvGm7iwSI5ULLe0yqe82FEt_X7xJkZzg&s',
    features: [
      'Todo lo del paquete Essential',
      '+ 2 Bocinas adicionales QSC K10 (1,000W)',
      '2 Micrófonos de mano',
      'Mayor cobertura y potencia',
    ],
  },
  {
    id: 'sono_premium',
    name: 'Premium',
    price: 17500,
    capacity: 'Hasta 1,000 personas',
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTZyc3an5bUF-Lg1HkgfH5H2GQyNud269KNZw&s',
    features: [
      'Todo lo del paquete Plus',
      '+ 2 Monitores de piso para stage',
      '+ Micrófono de podium',
      '+ 2 Bocinas adicionales',
      'Consola de Audio 16 canales Presonus StudioLive',
      '1 Ingeniero de audio + 1 Asistente',
      'Grabación de evento en audio incluida',
    ],
  },
];

export function SonorizacionSection({ isOpen, onToggle, data = {}, onChange, formatPrice, currency, settings = {} }) {
  const packages = PACKAGES.map(pkg => ({
    ...pkg,
    price: pkg.id === 'sono_essential' ? (settings.price_sono_essential || pkg.price) :
           pkg.id === 'sono_plus' ? (settings.price_sono_plus || pkg.price) :
           pkg.id === 'sono_premium' ? (settings.price_sono_premium || pkg.price) : pkg.price
  }));

  const selectedPkg = packages.find(p => p.id === data.package);
  const dias = data.dias || 0;
  const subtotal = selectedPkg?.price ? selectedPkg.price * dias : 0;

  const handleChange = (field, value) => onChange({ ...data, [field]: value });
  const badge = subtotal > 0 ? formatPrice(subtotal) : null;

  return (
    <CollapsibleSection title="Sonorización para Eventos en Vivo" icon={Speaker} isOpen={isOpen} onToggle={onToggle} badge={badge}>
      <div className="space-y-4">
        {/* Package Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {packages.filter(p => p.id !== null).map(pkg => {
            const isSelected = data.package === pkg.id;
            return (
              <button
                key={pkg.id}
                onClick={() => handleChange('package', isSelected ? null : pkg.id)}
                className={cn(
                  "flex flex-col rounded-xl border text-left transition-all overflow-hidden",
                  isSelected
                    ? "border-[#aa2aff] shadow-[0_0_20px_rgba(170,42,255,0.25)] bg-gradient-to-b from-[#1a1f50]/90 to-[#0a0e2a]"
                    : "border-white/10 bg-[#0f133a]/80 hover:border-[#aa2aff]/40"
                )}
                data-testid={`btn-sono-${pkg.id}`}
              >
                {/* Image */}
                {pkg.image && (
                  <div className="w-full h-28 overflow-hidden">
                    <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div>
                      <h4 className={cn("font-bold text-sm", isSelected ? "text-[#aa2aff]" : "text-white")}>{pkg.name}</h4>
                      <span className="text-slate-400 text-xs">{pkg.capacity}</span>
                    </div>
                    <span className="text-white font-bold text-sm">{formatPrice(pkg.price)}<span className="text-slate-400 text-xs">/día</span></span>
                  </div>
                  <ul className="space-y-1 mt-2">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <Check className="w-3 h-3 text-[#aa2aff] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300 text-[11px] leading-tight">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className={cn(
                    "mt-3 py-1.5 text-center text-xs font-semibold rounded-lg transition-all",
                    isSelected ? "bg-[#aa2aff] text-white" : "bg-white/5 text-slate-400"
                  )}>
                    {isSelected ? 'Seleccionado' : 'Seleccionar'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Days input */}
        {data.package && (
          <div className="bg-[#1a1f50]/50 rounded-xl p-4 border border-white/10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-white text-sm font-medium">¿Cuántos días de evento?</Label>
                <p className="text-slate-400 text-xs mt-0.5">Precio por día: {formatPrice(selectedPkg?.price || 0)} {currency}</p>
              </div>
              <Input
                type="number" min="1" max="30"
                value={data.dias || ''}
                onChange={(e) => handleChange('dias', parseInt(e.target.value) || 0)}
                placeholder="1"
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-24 h-10 focus:border-[#aa2aff]"
                data-testid="sonorizacion-dias"
              />
            </div>
          </div>
        )}

        {/* Subtotal */}
        {subtotal > 0 && (
          <div className="pt-4 border-t border-white/10 flex justify-between items-center text-lg">
            <span className="text-white font-semibold">Subtotal Sonorización:</span>
            <span className="text-[#aa2aff] font-bold">{formatPrice(subtotal)} {currency}</span>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function calcSonorizacionTotal(data = {}, settings = {}) {
  const prices = {
    'sono_essential': settings.price_sono_essential || 3500,
    'sono_plus': settings.price_sono_plus || 7500,
    'sono_premium': settings.price_sono_premium || 17500,
  };
  return (prices[data.package] || 0) * (data.dias || 0);
}
